/**
 * Bracket auto-generation utilities for competition incentives.
 *
 * Participant shape expected:
 *  { id: number|string, name: string }
 *
 * Bracket data structure returned:
 * {
 *   type: "single_elimination" | "double_elimination" | "king_of_the_hill",
 *   participant_count: N,
 *   rounds: [
 *     {
 *       round_number: 1,
 *       name: "Round 1",
 *       matchups: [
 *         {
 *           position: 1,            // slot number in the round
 *           participant_a: id | null,  // null = BYE
 *           participant_b: id | null,
 *           bye: boolean,           // true if one side is a BYE
 *           winner: null            // filled in later during contest
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * King of the Hill returns a slightly different shape:
 * {
 *   type: "king_of_the_hill",
 *   participant_count: N,
 *   champion: id,
 *   challenger_queue: [id, id, ...],
 *   current_matchup: { champion: id, challenger: id, winner: null },
 *   completed_matchups: []
 * }
 */

// ── helpers ──────────────────────────────────────────────────────────────────

/** Returns a human-readable name for a bracket round based on matchup count and whether it has BYEs */
function getRoundName(matchupCount, hasByes) {
  if (hasByes) return "Play-In";
  const participantsInRound = matchupCount * 2;
  if (participantsInRound === 2) return "Championship";
  if (participantsInRound === 4) return "Final Four";
  if (participantsInRound === 8) return "Elite Eight";
  if (participantsInRound === 16) return "Sweet 16";
  return `Round of ${participantsInRound}`;
}

/** Next power of 2 >= n */
function nextPow2(n) {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/**
 * Standard folded bracket slot order for a bracket of size `size`.
 * Returns 1-based slot indexes in the order they appear in Round 1
 * (classic fold: 1 vs last, 2 vs second-to-last, etc.).
 */
function buildSeedOrder(size) {
  let seeds = [1];
  while (seeds.length < size) {
    const nextSize = seeds.length * 2;
    const next = [];
    for (const s of seeds) {
      next.push(s);
      next.push(nextSize + 1 - s);
    }
    seeds = next;
  }
  return seeds;
}

/**
 * Shuffle an array copy using Fisher-Yates.
 */
function shuffleParticipants(participants) {
  const arr = [...participants];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ── Single Elimination ────────────────────────────────────────────────────────

/**
 * Generate a single-elimination bracket.
 * BYEs are inserted where needed so bracket size is always a power of 2.
 * Input participant order is randomized for this helper; for deterministic
 * designer-driven seeding, use generateBracketFromSlots().
 */
export function generateSingleElimination(participants) {
  const n = participants.length;
  const size = nextPow2(n);
  const byeCount = size - n;
  const seedOrder = buildSeedOrder(size);

  // Slot participants into folded round-1 order; any empty slots become BYEs.
  const slotted = new Array(size).fill(null);
  const seeded = shuffleParticipants(participants); // random seeding by default

  seedOrder.forEach((seed, index) => {
    const participantIndex = seed - 1; // 0-based
    slotted[index] =
      participantIndex < seeded.length ? seeded[participantIndex] : null;
  });

  // Build R1 matchups
  const r1Matchups = [];
  for (let i = 0; i < size; i += 2) {
    const a = slotted[i];
    const b = slotted[i + 1];
    const isBye = !a || !b;
    r1Matchups.push({
      position: i / 2 + 1,
      participant_a: a ? a.id : null,
      participant_b: b ? b.id : null,
      a_name: a ? a.name : "BYE",
      b_name: b ? b.name : "BYE",
      bye: isBye,
      winner: isBye ? (a ? a.id : b ? b.id : null) : null, // auto-advance on BYE
    });
  }

  const rounds = [
    {
      round_number: 1,
      name: getRoundName(r1Matchups.length, byeCount > 0),
      matchups: r1Matchups,
    },
  ];

  // Add placeholder rounds for the rest of the bracket
  let roundMatchups = r1Matchups.length;
  let roundNum = 2;
  while (roundMatchups > 1) {
    roundMatchups = Math.ceil(roundMatchups / 2);
    const placeholders = [];
    for (let i = 0; i < roundMatchups; i++) {
      placeholders.push({
        position: i + 1,
        participant_a: null,
        participant_b: null,
        a_name: "TBD",
        b_name: "TBD",
        bye: false,
        winner: null,
      });
    }
    rounds.push({
      round_number: roundNum,
      name: getRoundName(roundMatchups, false),
      matchups: placeholders,
    });
    roundNum++;
  }

  return {
    type: "single_elimination",
    participant_count: n,
    bracket_size: size,
    bye_count: byeCount,
    rounds,
  };
}

function generateSingleEliminationFromSlots(slottedParticipants) {
  const size = slottedParticipants.length;
  const participantCount = slottedParticipants.filter(Boolean).length;
  const byeCount = size - participantCount;

  const r1Matchups = [];
  for (let i = 0; i < size; i += 2) {
    const a = slottedParticipants[i] || null;
    const b = slottedParticipants[i + 1] || null;
    const isBye = !a || !b;
    r1Matchups.push({
      position: i / 2 + 1,
      participant_a: a ? a.id : null,
      participant_b: b ? b.id : null,
      a_name: a ? a.name : "BYE",
      b_name: b ? b.name : "BYE",
      bye: isBye,
      winner: isBye ? (a ? a.id : b ? b.id : null) : null,
    });
  }

  const rounds = [
    {
      round_number: 1,
      name: getRoundName(r1Matchups.length, byeCount > 0),
      matchups: r1Matchups,
    },
  ];

  let roundMatchups = r1Matchups.length;
  let roundNum = 2;
  while (roundMatchups > 1) {
    roundMatchups = Math.ceil(roundMatchups / 2);
    const placeholders = [];
    for (let i = 0; i < roundMatchups; i++) {
      placeholders.push({
        position: i + 1,
        participant_a: null,
        participant_b: null,
        a_name: "TBD",
        b_name: "TBD",
        bye: false,
        winner: null,
      });
    }
    rounds.push({
      round_number: roundNum,
      name: getRoundName(roundMatchups, false),
      matchups: placeholders,
    });
    roundNum++;
  }

  return {
    type: "single_elimination",
    participant_count: participantCount,
    bracket_size: size,
    bye_count: byeCount,
    rounds,
  };
}

// ── Double Elimination ────────────────────────────────────────────────────────

/**
 * Generate a double-elimination bracket.
 * Winners bracket is identical to single elimination.
 * Losers bracket is seeded with empty slots — to be populated as winners-bracket
 * losers fall down.
 */
export function generateDoubleElimination(participants) {
  const n = participants.length;
  const size = nextPow2(n);
  const singleBracket = generateSingleElimination(participants);

  // Losers bracket: 2*(rounds-1) rounds, each half the previous width
  const wRounds = singleBracket.rounds.length; // winners rounds
  const lRounds = (wRounds - 1) * 2; // losers rounds
  const losersBracket = [];

  for (let r = 0; r < lRounds; r++) {
    const capacity = Math.max(1, size / Math.pow(2, Math.floor(r / 2) + 1));
    const matchups = [];
    for (let i = 0; i < capacity; i++) {
      matchups.push({
        position: i + 1,
        participant_a: null,
        participant_b: null,
        a_name: "TBD",
        b_name: "TBD",
        bye: false,
        winner: null,
        source: r % 2 === 0 ? "drop_from_winners" : "loser_of_previous",
      });
    }
    losersBracket.push({
      round_number: r + 1,
      name: `Losers Round ${r + 1}`,
      matchups,
    });
  }

  return {
    type: "double_elimination",
    participant_count: n,
    bracket_size: size,
    bye_count: size - n,
    winners_bracket: { rounds: singleBracket.rounds },
    losers_bracket: { rounds: losersBracket },
    grand_final: {
      round_number: 1,
      name: "Grand Final",
      matchups: [
        {
          position: 1,
          participant_a: null,
          participant_b: null,
          a_name: "Winners Champion",
          b_name: "Losers Champion",
          bye: false,
          winner: null,
          note: "Winners champion may need 2 losses",
        },
      ],
    },
  };
}

// ── King of the Hill ──────────────────────────────────────────────────────────

/**
 * Generate a King of the Hill structure.
 * Random participant becomes starting champion; everyone else queues up.
 */
export function generateKingOfTheHill(participants) {
  const shuffled = shuffleParticipants(participants);
  const champion = shuffled[0];
  const queue = shuffled.slice(1);

  return {
    type: "king_of_the_hill",
    participant_count: participants.length,
    champion: champion.id,
    champion_name: champion.name,
    challenger_queue: queue.map(p => p.id),
    challenger_names: queue.reduce((acc, p) => {
      acc[p.id] = p.name;
      return acc;
    }, {}),
    current_matchup:
      queue.length > 0
        ? {
            champion: champion.id,
            champion_name: champion.name,
            challenger: queue[0].id,
            challenger_name: queue[0].name,
            winner: null,
          }
        : null,
    completed_matchups: [],
  };
}

export function generateBracketFromSlots(competitionType, slots) {
  if (!Array.isArray(slots)) return null;

  const participants = slots.filter(Boolean);
  if (participants.length < 2) return null;

  if (competitionType === "king_of_the_hill") {
    const champion = participants[0];
    const queue = participants.slice(1);
    return {
      type: "king_of_the_hill",
      participant_count: participants.length,
      champion: champion.id,
      champion_name: champion.name,
      challenger_queue: queue.map(p => p.id),
      challenger_names: queue.reduce((acc, p) => {
        acc[p.id] = p.name;
        return acc;
      }, {}),
      current_matchup:
        queue.length > 0
          ? {
              champion: champion.id,
              champion_name: champion.name,
              challenger: queue[0].id,
              challenger_name: queue[0].name,
              winner: null,
            }
          : null,
      completed_matchups: [],
    };
  }

  const desiredSize = nextPow2(Math.max(2, participants.length));
  const normalizedSlots = slots.slice(0, desiredSize);
  while (normalizedSlots.length < desiredSize) normalizedSlots.push(null);

  const single = generateSingleEliminationFromSlots(normalizedSlots);

  if (competitionType === "single_elimination") {
    return single;
  }

  if (competitionType === "double_elimination") {
    const size = normalizedSlots.length;
    const wRounds = single.rounds.length;
    const lRounds = (wRounds - 1) * 2;
    const losersBracket = [];

    for (let r = 0; r < lRounds; r++) {
      const capacity = Math.max(1, size / Math.pow(2, Math.floor(r / 2) + 1));
      const matchups = [];
      for (let i = 0; i < capacity; i++) {
        matchups.push({
          position: i + 1,
          participant_a: null,
          participant_b: null,
          a_name: "TBD",
          b_name: "TBD",
          bye: false,
          winner: null,
          source: r % 2 === 0 ? "drop_from_winners" : "loser_of_previous",
        });
      }
      losersBracket.push({
        round_number: r + 1,
        name: `Losers Round ${r + 1}`,
        matchups,
      });
    }

    return {
      type: "double_elimination",
      participant_count: single.participant_count,
      bracket_size: single.bracket_size,
      bye_count: single.bye_count,
      winners_bracket: { rounds: single.rounds },
      losers_bracket: { rounds: losersBracket },
      grand_final: {
        round_number: 1,
        name: "Grand Final",
        matchups: [
          {
            position: 1,
            participant_a: null,
            participant_b: null,
            a_name: "Winners Champion",
            b_name: "Losers Champion",
            bye: false,
            winner: null,
            note: "Winners champion may need 2 losses",
          },
        ],
      },
    };
  }

  return null;
}

// ── Entry point ───────────────────────────────────────────────────────────────

/**
 * Generate bracket for the given competition type and participant list.
 * Returns null if participants < 2.
 */
export function generateBracket(competitionType, participants) {
  if (!participants || participants.length < 2) return null;

  switch (competitionType) {
    case "single_elimination":
      return generateSingleElimination(participants);
    case "double_elimination":
      return generateDoubleElimination(participants);
    case "king_of_the_hill":
      return generateKingOfTheHill(participants);
    default:
      return null;
  }
}

/** Returns a human-readable round-by-round summary string */
export function describeBracket(bracketData) {
  if (!bracketData) return "";
  const { type, participant_count, bracket_size } = bracketData;
  const byeCount = bracket_size ? bracket_size - participant_count : 0;

  switch (type) {
    case "single_elimination":
      return `${participant_count} participants, ${bracketData.rounds.length} rounds${byeCount > 0 ? `, ${byeCount} BYE(s)` : ""}`;
    case "double_elimination":
      return `${participant_count} participants — Winners + Losers brackets, Grand Final`;
    case "king_of_the_hill":
      return `${participant_count} participants — 1 champion defends against ${participant_count - 1} challengers`;
    default:
      return "";
  }
}
